import { useMemo } from 'react';
import type { IPersonListItem, PersonKind } from '@skystone/shared';
import { Badge, DataTable, type ColumnDef } from '@/components/ui';
import { formatDate } from '@/lib/date-utils';
import { KindBadge } from './KindBadge';

interface PersonListTableProps {
  items: IPersonListItem[];
  onSelect: (person: IPersonListItem) => void;
}

function initials(displayName: string): string {
  const trimmed = displayName.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

type PersonStatus = 'Active' | 'Inactive' | 'Tombstoned';

// Status is a derived column — collapses (isTombstoned, isActive) to one
// human-readable string so the select filter can match by exact equality.
function personStatus(p: IPersonListItem): PersonStatus {
  if (p.isTombstoned) return 'Tombstoned';
  return p.isActive ? 'Active' : 'Inactive';
}

const KIND_OPTIONS: ReadonlyArray<{ label: string; value: PersonKind }> = [
  { label: 'Human', value: 'human' },
  { label: 'Bot', value: 'bot' },
  { label: 'Service', value: 'service' },
];

const STATUS_OPTIONS: ReadonlyArray<{ label: string; value: PersonStatus }> = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Tombstoned', value: 'Tombstoned' },
];

// Stateless cell renderers are hoisted to module scope (S6478): defining them
// inside the component would create a new function identity on every render.
// The displayName cell stays inline because it closes over the `onSelect`
// callback, which the DataTable `cell` API cannot otherwise supply.
function renderEmailCell(_value: unknown, row: IPersonListItem) {
  return <span className="text-muted-foreground">{row.primaryEmail ?? '—'}</span>;
}

function renderDepartmentCell(_value: unknown, row: IPersonListItem) {
  return <span className="text-muted-foreground">{row.department ?? '—'}</span>;
}

function renderJobTitleCell(_value: unknown, row: IPersonListItem) {
  return <span className="text-muted-foreground">{row.jobTitle ?? '—'}</span>;
}

function renderManagerCell(_value: unknown, row: IPersonListItem) {
  return <span className="text-muted-foreground">{row.managerName ?? '—'}</span>;
}

function renderKindCell(_value: unknown, row: IPersonListItem) {
  return <KindBadge kind={row.kind} />;
}

function renderLinkedCell(value: unknown) {
  return <span className="font-mono text-xs">{typeof value === 'number' ? value : 0}</span>;
}

function renderStatusCell(value: unknown) {
  const status = value as PersonStatus;
  if (status === 'Tombstoned') return <Badge variant="warning">Tombstoned</Badge>;
  if (status === 'Active') return <Badge variant="success">Active</Badge>;
  return <Badge variant="secondary">Inactive</Badge>;
}

function renderDeactivatedCell(_value: unknown, row: IPersonListItem) {
  return (
    <span className="text-muted-foreground">
      {row.deactivatedAt ? formatDate(row.deactivatedAt) : '—'}
    </span>
  );
}

interface DisplayNameCellProps {
  row: IPersonListItem;
  onSelect: (person: IPersonListItem) => void;
}

// Hoisted to module scope (S6478) and given `onSelect` as a prop so it no
// longer needs to be defined inline inside the column config. Null-Entra rows
// keep the click-to-open behaviour so users can still open the detail dialog
// to investigate "why is Entra missing?"; the avatar circle is dropped because
// an empty avatar next to the em-dash placeholder reads as noise.
function DisplayNameCell({ row, onSelect }: Readonly<DisplayNameCellProps>) {
  if (row.entraDisplayName === null) {
    return (
      <button
        type="button"
        onClick={() => onSelect(row)}
        className="text-left text-muted-foreground hover:underline"
      >
        —
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className="flex items-center gap-3 text-left font-medium text-foreground hover:underline"
    >
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {initials(row.entraDisplayName)}
      </span>
      <span className="truncate">{row.entraDisplayName}</span>
    </button>
  );
}

// Build the displayName cell renderer at module scope so the column config
// references a stable function rather than defining a component inline in the
// parent's render (S6478). `onSelect` is bound in via this factory.
function makeDisplayNameCell(
  onSelect: (person: IPersonListItem) => void,
): NonNullable<ColumnDef<IPersonListItem>['cell']> {
  return (_value, row) => <DisplayNameCell row={row} onSelect={onSelect} />;
}

export function PersonListTable({ items, onSelect }: Readonly<PersonListTableProps>) {
  const displayNameCell = useMemo(() => makeDisplayNameCell(onSelect), [onSelect]);

  // Department options are derived from the rows actually loaded. Persons
  // without an Entra link have `department === null` and drop out of the
  // dropdown — their rows remain reachable by clearing this filter.
  const departmentOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of items) {
      if (p.department) set.add(p.department);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((d) => ({ label: d, value: d }));
  }, [items]);

  const columns = useMemo<ColumnDef<IPersonListItem>[]>(
    () => [
      {
        id: 'displayName',
        header: 'Display name',
        // Sourced from `entra_users.display_name` via the Person's entra link
        // (PRODOPSAI-454). `Person.displayName` is unreliable here — for
        // Persons created from SSO/Entra import it was seeded with the
        // email-prefix or full email, so rendering it duplicated the Email
        // column. Persons without an Entra link render as `—`.
        accessor: (row) => row.entraDisplayName ?? '',
        sortable: true,
        filter: { type: 'text', placeholder: 'Filter name…' },
        cell: displayNameCell,
      },
      {
        id: 'primaryEmail',
        header: 'Email',
        accessor: (row) => row.primaryEmail ?? '',
        sortable: true,
        filter: { type: 'text', placeholder: 'Filter email…' },
        cell: renderEmailCell,
      },
      {
        id: 'department',
        header: 'Department',
        accessor: (row) => row.department ?? '',
        sortable: true,
        filter: { type: 'select', placeholder: 'All', options: departmentOptions },
        cell: renderDepartmentCell,
        hideBelow: 'md',
      },
      {
        id: 'jobTitle',
        header: 'Job title',
        accessor: (row) => row.jobTitle ?? '',
        sortable: true,
        filter: { type: 'text', placeholder: 'Filter title…' },
        cell: renderJobTitleCell,
        hideBelow: 'lg',
      },
      {
        id: 'managerName',
        header: 'Manager',
        accessor: (row) => row.managerName ?? '',
        sortable: true,
        filter: { type: 'text', placeholder: 'Filter manager…' },
        cell: renderManagerCell,
        hideBelow: 'lg',
      },
      {
        id: 'kind',
        header: 'Kind',
        accessor: 'kind',
        sortable: true,
        filter: { type: 'select', placeholder: 'All', options: [...KIND_OPTIONS] },
        cell: renderKindCell,
      },
      {
        id: 'linkedAccountCount',
        header: 'Linked',
        accessor: 'linkedAccountCount',
        sortable: true,
        align: 'right',
        cell: renderLinkedCell,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: personStatus,
        sortable: true,
        filter: { type: 'select', placeholder: 'All', options: [...STATUS_OPTIONS] },
        cell: renderStatusCell,
      },
      {
        id: 'deactivatedAt',
        header: 'Deactivated',
        // Set by the Entra dropout sweep (PRODOPSAI-487/-489); null for active
        // Persons. Surfaced so admins can see WHEN someone dropped out when the
        // "Show inactive" toggle reveals their row. Renders `—` when null.
        accessor: (row) => row.deactivatedAt ?? '',
        sortable: true,
        cell: renderDeactivatedCell,
        hideBelow: 'lg',
      },
    ],
    [displayNameCell, departmentOptions],
  );

  return (
    <DataTable<IPersonListItem>
      data={items}
      columns={columns}
      keyExtractor={(row) => row.id}
      emptyMessage="No people match the current filters."
      defaultPageSize={20}
      pageSizeOptions={[20, 50, 100]}
    />
  );
}
